/**
 * HX Ops — Cross-supplier Meetings section.
 *
 * Aggregates all supplier meetings into a single executive timeline with:
 *   • Search by title / attendee / agenda / notes
 *   • Filter by supplier
 *   • Pending follow-ups counter at the top
 *
 * Data source: GET /api/ops/meetings
 */
import React from "react";
import axios from "axios";
import {
  CalendarClock, Search, Users, ListChecks, Loader2, Filter, ChevronDown,
} from "lucide-react";
import { partnerBySlug } from "@/data/partners";

const P = {
  bg:      "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E4E7EB",
  dim:     "rgba(228,231,235,0.62)",
  accent:  "#3B82F6",
  accent2: "#22C55E",
  silver:  "#C0C7CE",
  amber:   "#F59E0B",
};

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const SUPPLIER_LABELS = {
  siemens:    "Siemens",
  vcow:       "VCOW",
  dfoptix:    "DFOPTIX",
  bluesun:    "Bluesun Solar",
  wattzing:   "Wattzing",
  beny:       "BENY New Energy",
  neura:      "NEURA Robotics",
  fraunhofer: "Fraunhofer",
};

const supplierLabel = (id) => SUPPLIER_LABELS[id] || id || "Unassigned";

const groupBySupplier = (meetings) => {
  const map = new Map();
  meetings.forEach((m) => {
    const sid = m.supplier_id || "unassigned";
    if (!map.has(sid)) map.set(sid, []);
    map.get(sid).push(m);
  });
  return Array.from(map.entries()).sort((a, b) => supplierLabel(a[0]).localeCompare(supplierLabel(b[0])));
};

const Card = ({ children, className = "" }) => (
  <div className={"rounded-lg p-4 " + className} style={{ background: P.surface, border: `1px solid ${P.border}` }}>
    {children}
  </div>
);

export default function MeetingsSection() {
  const [meetings, setMeetings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [supplierFilter, setSupplierFilter] = React.useState("all");

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/ops/meetings`);
        if (mounted) setMeetings(r.data || []);
      } catch { /* */ }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return meetings.filter((m) => {
      if (supplierFilter !== "all" && (m.supplier_id || "unassigned") !== supplierFilter) return false;
      if (!q) return true;
      const hay = [
        m.title, m.agenda, m.minutes,
        (m.attendees || []).join(" "),
        (m.follow_ups || []).join(" "),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [meetings, query, supplierFilter]);

  const followUpsCount = React.useMemo(
    () => meetings.reduce((sum, m) => sum + (m.follow_ups?.length || 0), 0),
    [meetings]
  );

  const grouped = groupBySupplier(filtered);
  const uniqueSuppliers = Array.from(new Set(meetings.map((m) => m.supplier_id || "unassigned")));

  return (
    <div data-testid="meetings-section">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.accent }}>Meetings</div>
        <div className="mt-2 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-bold tracking-tight text-[32px] lg:text-[40px] leading-tight" style={{ color: "#fff" }}>
              Relationship timeline.
            </h1>
            <p className="mt-2 text-[13.5px] max-w-2xl" style={{ color: P.dim }}>
              Every call, video meeting and site visit logged against a supplier. Filter, search and never lose a follow-up.
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.dim }}>Total meetings</div>
          <div className="mt-2 font-bold text-[28px]" style={{ color: "#fff" }} data-testid="meetings-kpi-total">{meetings.length}</div>
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.dim }}>Suppliers engaged</div>
          <div className="mt-2 font-bold text-[28px]" style={{ color: "#fff" }} data-testid="meetings-kpi-suppliers">{uniqueSuppliers.length}</div>
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.dim }}>Follow-ups tracked</div>
          <div className="mt-2 font-bold text-[28px]" style={{ color: P.accent2 }} data-testid="meetings-kpi-followups">{followUpsCount}</div>
        </Card>
        <Card>
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.dim }}>Last meeting</div>
          <div className="mt-2 font-semibold text-[15px]" style={{ color: "#fff" }} data-testid="meetings-kpi-last">
            {meetings[0] ? new Date(meetings[0].date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
          </div>
        </Card>
      </div>

      {/* Search + filters */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: P.silver }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, attendee, agenda…"
            data-testid="meetings-search"
            className="w-full pl-9 pr-3 py-2 text-[12.5px] rounded-md outline-none"
            style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
          />
        </div>
        <div className="relative">
          <Filter size={11} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: P.silver }} />
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            data-testid="meetings-filter-supplier"
            className="pl-8 pr-8 py-2 text-[12px] rounded-md outline-none appearance-none"
            style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
          >
            <option value="all">All suppliers</option>
            {uniqueSuppliers.map((sid) => (
              <option key={sid} value={sid}>{supplierLabel(sid)}</option>
            ))}
          </select>
          <ChevronDown size={11} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: P.silver }} />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }}>
          <Loader2 size={12} className="animate-spin" /> Loading meetings…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg p-8 text-center" style={{ background: P.surface, border: `1px dashed ${P.border}` }} data-testid="meetings-empty">
          <CalendarClock size={22} className="mx-auto mb-2" style={{ color: P.silver }} />
          <div className="text-[13px] font-semibold" style={{ color: "#fff" }}>
            {meetings.length === 0 ? "No meetings logged yet" : "No meetings match this filter"}
          </div>
          <div className="mt-1 text-[11.5px]" style={{ color: P.dim }}>
            {meetings.length === 0
              ? "Open any supplier and use the Meetings tab to log your first call or site visit."
              : "Try clearing the search or picking a different supplier."}
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([sid, items]) => {
            const supplierLogo = partnerBySlug(sid)?.logo;
            return (
            <div key={sid} data-testid={`meetings-group-${sid}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  {supplierLogo ? (
                    <div
                      className="w-7 h-7 rounded flex items-center justify-center shrink-0"
                      style={{ background: "rgba(255,255,255,0.94)" }}
                      data-testid={`meetings-group-${sid}-logo`}
                    >
                      <img src={supplierLogo} alt={`${supplierLabel(sid)} logo`} className="max-h-5 max-w-6 object-contain" />
                    </div>
                  ) : (
                    <div
                      className="w-7 h-7 rounded flex items-center justify-center shrink-0 font-bold text-[11px]"
                      style={{ background: `${P.accent}22`, color: P.accent }}
                    >
                      {supplierLabel(sid)[0]}
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.accent }}>
                    {supplierLabel(sid)} <span className="opacity-60">· {items.length}</span>
                  </div>
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.22em]" style={{ color: P.silver }}>
                  {items.reduce((s, m) => s + (m.follow_ups?.length || 0), 0)} follow-ups
                </div>
              </div>
              <div className="space-y-2">
                {items.map((m) => (
                  <div key={m.id}
                    data-testid={`meetings-row-${m.id}`}
                    className="p-4 rounded-lg" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
                    <div className="flex items-start gap-3">
                      <div className="text-center shrink-0 min-w-[54px]">
                        <div className="text-[10px] font-mono uppercase" style={{ color: P.silver }}>
                          {new Date(m.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                        </div>
                        <div className="text-[9px]" style={{ color: P.dim }}>{new Date(m.date).getFullYear()}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13.5px] font-semibold" style={{ color: "#fff" }}>{m.title}</div>
                        {m.attendees?.length > 0 && (
                          <div className="mt-1 flex items-center gap-1.5 text-[11px]" style={{ color: P.dim }}>
                            <Users size={10} /> {m.attendees.join(" · ")}
                          </div>
                        )}
                        {m.agenda && (
                          <div className="mt-2 text-[12px] whitespace-pre-wrap" style={{ color: P.ink }}>{m.agenda}</div>
                        )}
                        {m.follow_ups?.length > 0 && (
                          <div className="mt-2">
                            <div className="text-[9px] uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color: P.accent2 }}>
                              <ListChecks size={10} /> Follow-ups
                            </div>
                            <ul className="text-[12px] pl-4 list-disc" style={{ color: P.ink }}>
                              {m.follow_ups.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
